import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  Grid,
  Button,
  TextField,
  AppBar,
  Toolbar,
  Avatar,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText
} from '@mui/material';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, useParams } from 'react-router-dom';
import { studentService, Student } from '../services/studentService';
import userService from '../services/userService';
import { toast } from 'react-toastify';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';

interface University {
  id: number;
  name: string;
}

interface Provider {
  userId: number;
  firstName: string;
  lastName: string;
}

interface FormData {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  schoolId: string;
  address: string;
  studyProgram: string;
  gender: string;
  dateOfBirth: Date | null;
  school: string;
  race: string;
  emergencyContactName: string;
  emergencyContactRelation: string;
  emergencyContactPhone: string;
  universityId: number | null;
  providerId: number | null;
  password: string;
}

const StudentEditPage: React.FC = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [student, setStudent] = useState<Student | null>(null);
  const [universities, setUniversities] = useState<University[]>([]);
  const [providers, setProviders] = useState<Provider[]>([]);
  const [formData, setFormData] = useState<FormData>({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    schoolId: '',
    address: '',
    studyProgram: '',
    gender: '',
    dateOfBirth: null,
    school: '',
    race: '',
    emergencyContactName: '',
    emergencyContactRelation: '',
    emergencyContactPhone: '',
    universityId: null,
    providerId: null,
    password: ''
  });
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    loadData();
  }, [id]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);

      const studentId = parseInt(id || '0');
      if (!studentId) {
        throw new Error('Invalid student ID');
      }

      // Load student data and other required data
      const [studentData, universitiesData, providersData] = await Promise.all([
        studentService.getStudent(studentId),
        userService.getUniversities(),
        userService.getProviders()
      ]);

      setStudent(studentData);
      setUniversities(universitiesData);
      setProviders(providersData);

      // Populate form with student data
      setFormData({
        firstName: studentData.firstName || '',
        lastName: studentData.lastName || '',
        email: studentData.email || '',
        phone: studentData.phone || '',
        schoolId: studentData.schoolId || '',
        address: studentData.address || '',
        studyProgram: studentData.studyProgram || '',
        gender: studentData.gender || '',
        dateOfBirth: studentData.dateOfBirth ? new Date(studentData.dateOfBirth) : null,
        school: studentData.school || '',
        race: studentData.race || '',
        emergencyContactName: studentData.emergencyContactName || '',
        emergencyContactRelation: studentData.emergencyContactRelation || '',
        emergencyContactPhone: studentData.emergencyContactPhone || '',
        universityId: studentData.universityId || null,
        providerId: studentData.providerId || null,
        password: '' // Don't populate password
      });
    } catch (err: any) {
      setError(err.message || 'Failed to load student data');
      toast.error('Failed to load student data');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field: keyof FormData) => (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | { target: { value: any } }
  ) => {
    setFormData({
      ...formData,
      [field]: event.target.value
    });
    // Clear validation error for this field
    if (validationErrors[field]) {
      setValidationErrors({
        ...validationErrors,
        [field]: ''
      });
    }
  };

  const handleDateChange = (date: Date | null) => {
    setFormData({
      ...formData,
      dateOfBirth: date
    });
  };

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};

    if (!formData.firstName.trim()) {
      errors.firstName = 'First name is required';
    }
    if (!formData.lastName.trim()) {
      errors.lastName = 'Last name is required';
    }
    if (!formData.email.trim()) {
      errors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      errors.email = 'Email is invalid';
    }
    if (!formData.schoolId.trim()) {
      errors.schoolId = 'School ID is required';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      setSaving(true);
      setError(null);

      const studentId = parseInt(id || '0');
      
      // Prepare update data
      const updateData: any = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        phone: formData.phone,
        schoolId: formData.schoolId,
        address: formData.address,
        studyProgram: formData.studyProgram,
        gender: formData.gender,
        dateOfBirth: formData.dateOfBirth ? formData.dateOfBirth.toISOString().split('T')[0] : null,
        school: formData.school,
        race: formData.race,
        emergencyContactName: formData.emergencyContactName,
        emergencyContactRelation: formData.emergencyContactRelation,
        emergencyContactPhone: formData.emergencyContactPhone,
        universityId: formData.universityId,
        providerId: formData.providerId
      };

      // Only include password if it's been changed
      if (formData.password) {
        updateData.password = formData.password;
      }

      await studentService.updateStudent(studentId, updateData);
      toast.success('Student updated successfully');
      navigate('/students', { replace: true });
    } catch (err: any) {
      setError(err.message || 'Failed to update student');
      toast.error('Failed to update student');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    navigate('/students', { replace: true });
  };

  const handleLogout = () => {
    logout();
  };

  // Check if user can edit this student
  const canEdit = user?.userType === 'admin' || 
    (user?.userType === 'provider' && student?.providerId === user?.userId);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress />
      </Box>
    );
  }

  if (!canEdit) {
    return (
      <Container>
        <Alert severity="error" sx={{ mt: 4 }}>
          You don't have permission to edit this student.
        </Alert>
        <Button onClick={() => navigate('/students', { replace: true })} sx={{ mt: 2 }}>
          Back to Students
        </Button>
      </Container>
    );
  }

  return (
    <>
      <AppBar position="static">
        <Toolbar>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            Edit Student
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Avatar sx={{ bgcolor: 'secondary.main' }}>
              {user?.firstName?.charAt(0) || 'U'}
            </Avatar>
            <Typography variant="body1">
              {user?.firstName} {user?.lastName}
            </Typography>
            <Button color="inherit" onClick={handleLogout}>
              Logout
            </Button>
          </Box>
        </Toolbar>
      </AppBar>

      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Box sx={{ mb: 3 }}>
          <Typography variant="h4" gutterBottom>
            Edit Student
          </Typography>
          <Button onClick={handleCancel} sx={{ mb: 2 }}>
            ← Back to Students
          </Button>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Card>
          <CardContent>
            <form onSubmit={handleSubmit}>
              <Grid container spacing={3}>
                {/* Basic Information */}
                <Grid item xs={12}>
                  <Typography variant="h6" gutterBottom>
                    Basic Information
                  </Typography>
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="First Name"
                    value={formData.firstName}
                    onChange={handleInputChange('firstName')}
                    error={!!validationErrors.firstName}
                    helperText={validationErrors.firstName}
                    required
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Last Name"
                    value={formData.lastName}
                    onChange={handleInputChange('lastName')}
                    error={!!validationErrors.lastName}
                    helperText={validationErrors.lastName}
                    required
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Email"
                    type="email"
                    value={formData.email}
                    onChange={handleInputChange('email')}
                    error={!!validationErrors.email}
                    helperText={validationErrors.email}
                    required
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Phone"
                    value={formData.phone}
                    onChange={handleInputChange('phone')}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="School ID"
                    value={formData.schoolId}
                    onChange={handleInputChange('schoolId')}
                    error={!!validationErrors.schoolId}
                    helperText={validationErrors.schoolId}
                    required
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Password (leave blank to keep current)"
                    type="password"
                    value={formData.password}
                    onChange={handleInputChange('password')}
                    helperText="Only fill this if you want to change the password"
                  />
                </Grid>

                {/* Academic Information */}
                <Grid item xs={12}>
                  <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                    Academic Information
                  </Typography>
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Study Program"
                    value={formData.studyProgram}
                    onChange={handleInputChange('studyProgram')}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Year in School"
                    value={formData.school}
                    onChange={handleInputChange('school')}
                  />
                </Grid>

                {user?.userType === 'admin' && (
                  <>
                    <Grid item xs={12} md={6}>
                      <FormControl fullWidth>
                        <InputLabel>University</InputLabel>
                        <Select
                          value={formData.universityId || ''}
                          onChange={(e) => handleInputChange('universityId')(e as any)}
                        >
                          <MenuItem value="">None</MenuItem>
                          {universities.map((univ) => (
                            <MenuItem key={univ.id} value={univ.id}>
                              {univ.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <FormControl fullWidth>
                        <InputLabel>Provider</InputLabel>
                        <Select
                          value={formData.providerId || ''}
                          onChange={(e) => handleInputChange('providerId')(e as any)}
                        >
                          <MenuItem value="">Unassigned</MenuItem>
                          {providers.map((provider) => (
                            <MenuItem key={provider.userId} value={provider.userId}>
                              {provider.firstName} {provider.lastName}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                  </>
                )}

                {/* Personal Information */}
                <Grid item xs={12}>
                  <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                    Personal Information
                  </Typography>
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Address"
                    value={formData.address}
                    onChange={handleInputChange('address')}
                    multiline
                    rows={2}
                  />
                </Grid>

                <Grid item xs={12} md={4}>
                  <FormControl fullWidth>
                    <InputLabel>Gender</InputLabel>
                    <Select
                      value={formData.gender || ''}
                      onChange={(e) => handleInputChange('gender')(e as any)}
                    >
                      <MenuItem value="">Select</MenuItem>
                      <MenuItem value="MALE">Male</MenuItem>
                      <MenuItem value="FEMALE">Female</MenuItem>
                      <MenuItem value="OTHER">Other</MenuItem>
                      <MenuItem value="PREFER_NOT_TO_SAY">Prefer not to say</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} md={4}>
                  <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <DatePicker
                      label="Date of Birth"
                      value={formData.dateOfBirth}
                      onChange={handleDateChange}
                      slotProps={{
                        textField: {
                          fullWidth: true
                        }
                      }}
                    />
                  </LocalizationProvider>
                </Grid>

                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Race/Ethnicity"
                    value={formData.race}
                    onChange={handleInputChange('race')}
                  />
                </Grid>

                {/* Emergency Contact */}
                <Grid item xs={12}>
                  <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                    Emergency Contact
                  </Typography>
                </Grid>

                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Emergency Contact Name"
                    value={formData.emergencyContactName}
                    onChange={handleInputChange('emergencyContactName')}
                  />
                </Grid>

                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Relationship"
                    value={formData.emergencyContactRelation}
                    onChange={handleInputChange('emergencyContactRelation')}
                  />
                </Grid>

                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Emergency Contact Phone"
                    value={formData.emergencyContactPhone}
                    onChange={handleInputChange('emergencyContactPhone')}
                  />
                </Grid>

                {/* Action Buttons */}
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 2 }}>
                    <Button
                      variant="outlined"
                      onClick={handleCancel}
                      disabled={saving}
                    >
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      variant="contained"
                      disabled={saving}
                    >
                      {saving ? <CircularProgress size={20} /> : 'Save Changes'}
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </form>
          </CardContent>
        </Card>
      </Container>
    </>
  );
};

export default StudentEditPage;